import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, Star, Sparkles, Brain, Compass, Users, MessageCircle,
  ChevronRight, RefreshCw, X, Check, Zap, Target, Globe,
  Music, Book, Camera, Code, Palette, Briefcase, Dumbbell,
  Coffee, Plane, Film, Gamepad2, Mic, PenTool, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { FloatingBackButton } from '../components/layout/FloatingBackButton';

// Glassmorphism keyframes
const soulKeyframes = `
  @keyframes soulPulse {
    0%, 100% { transform: scale(1); opacity: 0.8; }
    50% { transform: scale(1.05); opacity: 1; }
  }
  @keyframes orbitalFloat {
    0% { transform: rotate(0deg) translateX(60px) rotate(0deg); }
    100% { transform: rotate(360deg) translateX(60px) rotate(-360deg); }
  }
  @keyframes connectionLine {
    0% { stroke-dashoffset: 100; opacity: 0; }
    50% { opacity: 1; }
    100% { stroke-dashoffset: 0; opacity: 1; }
  }
  @keyframes heartbeat {
    0%, 100% { transform: scale(1); }
    10% { transform: scale(1.1); }
    20% { transform: scale(1); }
    30% { transform: scale(1.1); }
    40% { transform: scale(1); }
  }
`;

// Compatibility Categories
const COMPATIBILITY_TRAITS = [
  { id: 'thinking', name: 'Thinking Style', icon: Brain, description: 'How you process information' },
  { id: 'values', name: 'Core Values', icon: Heart, description: 'What matters most to you' },
  { id: 'goals', name: 'Life Goals', icon: Target, description: 'Where you want to be' },
  { id: 'energy', name: 'Energy Level', icon: Zap, description: 'How you recharge' },
  { id: 'creativity', name: 'Creativity', icon: Palette, description: 'Your creative expression' },
];

// Soul Card Component
const SoulCard = ({ soul, onConnect, onSkip }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="relative"
    >
      <div 
        className="rounded-3xl overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          boxShadow: '0 8px 32px rgba(99, 102, 241, 0.15)',
        }}
      >
        {/* Header with Avatar */}
        <div className="relative h-48 overflow-hidden">
          <img 
            src={soul.picture} 
            alt={soul.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f23] via-transparent to-transparent" />
          
          {/* Compatibility Badge */}
          <div 
            className="absolute top-4 right-4 px-4 py-2 rounded-2xl flex items-center gap-2"
            style={{
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.9), rgba(139, 92, 246, 0.9))',
              boxShadow: '0 4px 20px rgba(99, 102, 241, 0.5)',
            }}
          >
            <Sparkles className="w-4 h-4 text-white" />
            <span className="text-white font-bold">{soul.compatibility}%</span>
          </div>

          {/* Soul Connection Animation */}
          <div className="absolute top-4 left-4">
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.8), rgba(139, 92, 246, 0.8))',
                animation: 'soulPulse 3s ease-in-out infinite',
              }}
            >
              <Brain className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Name & Location */}
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                {soul.name}
                {soul.verified && <Shield className="w-4 h-4 text-indigo-400" />}
              </h3>
              <p className="text-gray-400 text-sm flex items-center gap-1">
                <Globe className="w-3 h-3" />
                {soul.location}
              </p>
            </div>
            <span className="text-xs text-gray-500">{soul.age} years</span>
          </div>

          {/* Thinking Pattern Match */}
          <div className="mb-4 p-3 rounded-2xl bg-white/5 border border-white/10">
            <p className="text-xs text-gray-400 mb-1">Thinking Pattern</p>
            <p className="text-white text-sm font-medium">{soul.thinkingPattern}</p>
          </div>

          {/* Shared Traits */}
          <div className="mb-4">
            <p className="text-xs text-gray-400 mb-2">Shared Life Philosophy</p>
            <div className="flex flex-wrap gap-2">
              {soul.sharedTraits.map((trait) => (
                <span 
                  key={trait}
                  className="px-3 py-1 rounded-full text-xs"
                  style={{
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(139, 92, 246, 0.2))',
                    border: '1px solid rgba(99, 102, 241, 0.3)',
                    color: '#a5b4fc',
                  }}
                >
                  {trait}
                </span>
              ))}
            </div>
          </div>

          {/* Compatibility Breakdown */}
          <button 
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full flex items-center justify-between text-gray-400 text-sm mb-4"
          >
            <span>Compatibility Breakdown</span>
            <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          </button>

          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="space-y-2 mb-4 overflow-hidden"
              >
                {COMPATIBILITY_TRAITS.map(trait => {
                  const score = soul.compatibilityBreakdown?.[trait.id] || Math.floor(Math.random() * 30) + 70;
                  return (
                    <div key={trait.id} className="flex items-center gap-3">
                      <trait.icon className="w-4 h-4 text-gray-500" />
                      <div className="flex-1">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-400">{trait.name}</span>
                          <span className="text-indigo-400">{score}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                          <div 
                            className="h-full rounded-full"
                            style={{
                              width: `${score}%`,
                              background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Bio */}
          <p className="text-gray-400 text-sm mb-4 line-clamp-2">{soul.bio}</p>

          {/* Interests */}
          <div className="flex flex-wrap gap-1 mb-4">
            {soul.interests.slice(0, 4).map((interest) => (
              <span key={interest} className="px-2 py-1 rounded-lg bg-white/5 text-[10px] text-gray-400">
                {interest}
              </span>
            ))}
            {soul.interests.length > 4 && (
              <span className="px-2 py-1 rounded-lg bg-white/5 text-[10px] text-gray-500">
                +{soul.interests.length - 4}
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={() => onSkip?.(soul)}
              className="flex-1 py-3 rounded-2xl bg-white/5 border border-white/10 text-gray-400 font-medium flex items-center justify-center gap-2"
            >
              <X className="w-5 h-5" />
              Skip
            </button>
            <button
              onClick={() => onConnect?.(soul)}
              className="flex-1 py-3 rounded-2xl font-semibold flex items-center justify-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                boxShadow: '0 4px 20px rgba(99, 102, 241, 0.4)',
              }}
            >
              <Heart className="w-5 h-5" />
              Connect
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Sample Souls Data
const SAMPLE_SOULS = [
  {
    id: 'soul1',
    name: 'Aria Chen',
    age: 28,
    picture: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop',
    location: 'San Francisco, USA',
    compatibility: 94,
    verified: true,
    thinkingPattern: 'Creative Visionary - Sees patterns others miss',
    sharedTraits: ['Growth Mindset', 'Empathy First', 'Purpose Driven'],
    bio: 'Filmmaker and storyteller. I believe in the power of narratives to change the world. Always seeking deeper connections.',
    interests: ['Filmmaking', 'Philosophy', 'Travel', 'Psychology', 'Music', 'Art'],
    compatibilityBreakdown: {
      thinking: 96,
      values: 92,
      goals: 94,
      energy: 88,
      creativity: 98,
    }
  },
  {
    id: 'soul2',
    name: 'Marcus Rivera',
    age: 31,
    picture: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop',
    location: 'Barcelona, Spain',
    compatibility: 89,
    verified: true,
    thinkingPattern: 'Analytical Dreamer - Logic meets imagination',
    sharedTraits: ['Lifelong Learner', 'Authentic Living', 'Impact Focus'],
    bio: 'Tech entrepreneur by day, musician by night. Building products that matter while exploring the intersection of art and technology.',
    interests: ['Technology', 'Music', 'Startups', 'Meditation', 'Surfing'],
    compatibilityBreakdown: {
      thinking: 91,
      values: 88,
      goals: 92,
      energy: 85,
      creativity: 89,
    }
  },
  {
    id: 'soul3',
    name: 'Luna Park',
    age: 26,
    picture: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&h=400&fit=crop',
    location: 'Seoul, Korea',
    compatibility: 91,
    verified: false,
    thinkingPattern: 'Intuitive Creator - Feels before thinks',
    sharedTraits: ['Deep Thinker', 'Creative Soul', 'Curious Mind'],
    bio: 'Artist exploring the boundaries of digital and traditional media. Looking for minds that see the world differently.',
    interests: ['Art', 'Design', 'Fashion', 'Photography', 'Poetry', 'K-Drama'],
    compatibilityBreakdown: {
      thinking: 88,
      values: 94,
      goals: 90,
      energy: 92,
      creativity: 96,
    }
  },
];

// Main Component
export default function EchoSoulsPage() {
  const { user } = useAuth();
  const [souls, setSouls] = useState(SAMPLE_SOULS);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [connections, setConnections] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(true);

  // Inject keyframes
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = soulKeyframes;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleConnect = (soul) => {
    setConnections(prev => [...prev, soul]);
    toast.success(`Connection request sent to ${soul.name}!`);
    handleNext();
  };

  const handleSkip = (soul) => {
    handleNext();
  };

  const handleNext = () => {
    if (currentIndex < souls.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      toast.info('No more souls to discover. Check back later!');
    }
  };

  const currentSoul = souls[currentIndex];

  return (
    <div 
      className="min-h-screen text-white overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0f0f23, #1a1a3e, #0f0f23)',
      }}
      data-testid="echo-souls-page"
    >
      {/* Floating Back Button */}
      <FloatingBackButton />

      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute w-[500px] h-[500px] rounded-full opacity-15 blur-3xl"
          style={{
            background: 'radial-gradient(circle, #ec4899, transparent)',
            top: '5%',
            right: '-15%',
            animation: 'soulPulse 8s ease-in-out infinite',
          }}
        />
        <div 
          className="absolute w-[400px] h-[400px] rounded-full opacity-10 blur-3xl"
          style={{
            background: 'radial-gradient(circle, #6366f1, transparent)',
            bottom: '10%',
            left: '-10%',
            animation: 'soulPulse 10s ease-in-out infinite reverse',
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-10 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
            Echo Souls
          </h1>
          <p className="text-gray-500 text-sm">Discover your digital twins</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setCurrentIndex(0)}
            className="p-2 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10"
          >
            <RefreshCw className="w-5 h-5 text-gray-400" />
          </button>
          <div className="px-3 py-1.5 rounded-full bg-pink-500/20 border border-pink-500/30">
            <span className="text-pink-400 text-sm font-medium">{connections.length} Connected</span>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="relative z-10 px-6 py-2">
        <div 
          className="p-4 rounded-2xl flex items-center justify-around"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{souls.length}</p>
            <p className="text-xs text-gray-500">Souls Found</p>
          </div>
          <div className="w-px h-10 bg-white/10" />
          <div className="text-center">
            <p className="text-2xl font-bold text-indigo-400">{Math.round(souls.reduce((a, s) => a + s.compatibility, 0) / souls.length)}%</p>
            <p className="text-xs text-gray-500">Avg Match</p>
          </div>
          <div className="w-px h-10 bg-white/10" />
          <div className="text-center">
            <p className="text-2xl font-bold text-pink-400">{connections.length}</p>
            <p className="text-xs text-gray-500">Connections</p>
          </div>
        </div>
      </div>

      {/* AI Analysis Banner */}
      {!analysisComplete && (
        <div className="relative z-10 px-6 py-2">
          <div 
            className="p-4 rounded-2xl flex items-center gap-4"
            style={{
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(139, 92, 246, 0.2))',
              border: '1px solid rgba(99, 102, 241, 0.3)',
            }}
          >
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ animation: 'soulPulse 2s ease-in-out infinite' }}
            >
              <Brain className="w-6 h-6 text-indigo-400" />
            </div>
            <div className="flex-1">
              <p className="text-white font-medium text-sm">AI Analyzing Your Soul</p>
              <p className="text-gray-400 text-xs">Understanding your personality, values & goals...</p>
            </div>
            <div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      )}

      {/* Soul Cards */}
      <div className="relative z-10 px-6 py-4 pb-32">
        {currentSoul && (
          <AnimatePresence mode="wait">
            <SoulCard
              key={currentSoul.id}
              soul={currentSoul}
              onConnect={handleConnect}
              onSkip={handleSkip}
            />
          </AnimatePresence>
        )}

        {/* Progress */}
        <div className="mt-4 flex items-center justify-center gap-2">
          {souls.map((_, idx) => (
            <div
              key={idx}
              className={`h-1.5 rounded-full transition-all ${
                idx === currentIndex ? 'w-8 bg-indigo-500' : idx < currentIndex ? 'w-4 bg-indigo-500/50' : 'w-4 bg-white/10'
              }`}
            />
          ))}
        </div>

        {/* How It Works */}
        <div className="mt-8">
          <h3 className="text-gray-400 text-sm font-medium mb-4 flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            How Echo Souls Works
          </h3>
          <div className="grid grid-cols-3 gap-3">
            {[
              { icon: Brain, title: 'AI Analysis', desc: 'We analyze your personality & values' },
              { icon: Compass, title: 'Soul Matching', desc: 'Find digital twins worldwide' },
              { icon: Heart, title: 'Connect', desc: 'Build meaningful relationships' },
            ].map((step) => (
              <div
                key={step.title}
                className="p-3 rounded-2xl text-center"
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                }}
              >
                <div 
                  className="w-10 h-10 rounded-xl mx-auto mb-2 flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(139, 92, 246, 0.2))' }}
                >
                  <step.icon className="w-5 h-5 text-indigo-400" />
                </div>
                <p className="text-white text-[11px] font-medium mb-1">{step.title}</p>
                <p className="text-gray-500 text-[9px]">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
